import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { MotionConfig } from "motion/react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { App } from "@/app/App";
import { ToastProvider } from "@/components/ui";
import { ApiError } from "@/lib/api";
import { dashboardQuery } from "@/lib/queries/views";
import { applyTheme, watchSystemTheme } from "@/lib/theme";
import { registerServiceWorker } from "@/pwa/register";
import "@/styles/index.css";

applyTheme();
watchSystemTheme();
registerServiceWorker();

const sentryDsn: string | undefined = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  // Loaded a few seconds after the page so its ~30 kB gzip stay off Inicio's first paint (#67);
  // an error in those first seconds isn't reported
  window.addEventListener("load", () => {
    setTimeout(() => {
      void import("@sentry/react").then((Sentry) =>
        Sentry.init({ dsn: sentryDsn, tracesSampleRate: 0.1 }),
      );
    }, 3000);
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // only the dashboard refetches on focus (see dashboardQuery); the rest when visited
      refetchOnWindowFocus: false,
      retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
    },
  },
});

// Inicio's numbers load alongside /auth/me instead of after it (#67); logged out, it's one 401
if (window.location.pathname === "/") void queryClient.prefetchQuery(dashboardQuery());

const root = document.getElementById("root");
if (!root) throw new Error("#root missing");

createRoot(root).render(
  <StrictMode>
    <MotionConfig reducedMotion="user">
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ToastProvider>
            <App />
          </ToastProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </MotionConfig>
  </StrictMode>,
);
